import { formatAmount } from "@utils";
import RollingNumber from "./RollingNumber";
import PeriodSelect from "./PeriodSelect";
import { useTaskMetrics, useMetricPeriod, METRIC_PERIODS } from "../hooks/useTaskMetrics";

// Counter tiles under the collected figure. Each reads a metric key off the response; a metric with
// no answer (service unavailable) reads as 0.
const COUNTER_TILES = [
  { key: "requestsSent", label: "requests" },
  { key: "documentsSent", label: "documents" },
  { key: "aiAnswers", label: "prompts" },
  { key: "actionsCompleted", label: "actions" },
];

const Tile = ({ label, value, loading, persistKey }) => (
  <div className="flex flex-col gap-0.5 p-3.5">
    {loading ? (
      <span className="h-5 w-10 animate-pulse rounded bg-zinc-200" />
    ) : (
      <RollingNumber value={value} className="text-lg font-semibold text-zinc-800" persistKey={persistKey} />
    )}
    <span className="text-[13px] text-zinc-500">{label}</span>
  </div>
);

const MetricsPanel = () => {
  const [period, setPeriod] = useMetricPeriod("tasks.metricPeriod.panel");
  const { data, isLoading } = useTaskMetrics(period);
  const activePeriod = METRIC_PERIODS.find((p) => p.key === period) ?? METRIC_PERIODS[0];

  // A metric with no answer (service unavailable, or whole-panel error) reads as 0, not a dash.
  const valueFor = (key) => data?.[key] ?? 0;

  return (
    <section className="flex shrink-0 flex-col">
      <div className="rounded-2xl border border-zinc-200 bg-white">
        <div className="px-4 pt-4">
          <PeriodSelect
            value={period}
            onChange={setPeriod}
            position="left"
            label={activePeriod.collectedLabel}
            buttonClassName="flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 hover:text-zinc-600"
          />
        </div>

        <div className="px-4 pb-2 pt-1.5">
          {isLoading ? (
            <span className="mt-1 block h-7 w-36 animate-pulse rounded bg-zinc-200" />
          ) : (
            <RollingNumber
              value={data?.collected ?? 0}
              format={formatAmount}
              className="block text-2xl font-semibold text-zinc-900"
              persistKey="metrics.collected.panel"
            />
          )}
        </div>

        <div className="grid grid-cols-2 divide-x divide-y divide-zinc-100 border-t border-zinc-100">
          {COUNTER_TILES.map((tile) => (
            <Tile
              key={tile.key}
              label={tile.label}
              loading={isLoading}
              value={valueFor(tile.key)}
              persistKey={`metrics.tile.${tile.key}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MetricsPanel;
